import React, { useEffect, useState, useRef } from "react"
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import Select from "react-select"
import { useDispatch, useSelector } from "react-redux"
import FormLabel from "components/Extra/FormLabel"
import { FormInput } from "components/Extra/FormInput"
import { useTranslation } from "react-i18next"
import { createCoupon, updateCoupon, getCoupon } from "store/coupons/actions"
import { getDiscountTypes } from "store/discount-types/actions"
import { productOptions } from "pages/vendor/Products"

interface AddNewCouponProps {
  toggle: () => void
  isOpen: boolean
  couponId?: number // Optional: when provided, modal is in edit mode
}

export const AddNewCoupon: React.FC<AddNewCouponProps> = ({
  isOpen,
  toggle,
  couponId,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  // Determine if in edit mode
  const isEditMode = !!couponId

  // Get discount types from Redux store
  const { discountTypes } = useSelector((state: any) => state.DiscountTypes)

  // Get selected coupon from Redux store (for edit mode)
  const { selectedCoupon } = useSelector((state: any) => state.Coupons)

  // Fetch discount types on mount
  useEffect(() => {
    dispatch(getDiscountTypes({ push: (path: string) => {} } as any))
  }, [])

  // Fetch coupon data when modal opens in edit mode ONLY if not already in Redux
  useEffect(() => {
    if (isOpen && isEditMode && couponId) {
      // Check if we already fetched this specific coupon
      if (fetchedCouponIdRef.current !== couponId) {
        // Only fetch if we don't have the coupon data or if it's a different coupon
        if (!selectedCoupon || selectedCoupon.id !== couponId) {
          dispatch(getCoupon(couponId, { push: (path: string) => {} } as any))
          fetchedCouponIdRef.current = couponId
        }
      }
    }
    // Reset ref when modal closes
    if (!isOpen) {
      fetchedCouponIdRef.current = null
    }
  }, [isOpen, couponId, isEditMode])

  // Discount type options for dropdown
  const discountTypeOptions = discountTypes.map((dt: any) => ({
    value: dt.id,
    label: dt.enName || dt.arName || "__",
  }))

  // Original UI state
  const [openFirstAccordion, setOpenFirstAccordion] = useState("0")
  const [openThirdAccordion, setOpenThirdAccordion] = useState("0")
  const [selectedProduct, setSelectedProduct] = useState<{
    value: string
    label: string
  } | null>(null)
  const [key, setKey] = useState(0) // Force remount to reset form
  const fetchedCouponIdRef = useRef<number | null>(null) // Track fetched coupon to prevent duplicates

  const validation = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialValues: {
      // Backend fields (required for API)
      code: isEditMode && selectedCoupon ? selectedCoupon.code : "",
      enName: isEditMode && selectedCoupon ? selectedCoupon.enName : "",
      arName: isEditMode && selectedCoupon ? selectedCoupon.arName : "",
      discountValue: isEditMode && selectedCoupon ? selectedCoupon.discountValue : 0,
      discountTypeId: isEditMode && selectedCoupon ? selectedCoupon.discountTypeId : null,
      expiryDate: isEditMode && selectedCoupon ? selectedCoupon.expiryDate?.split("T")[0] || "" : "",
      isActive: isEditMode && selectedCoupon ? selectedCoupon.isActive : true,
      // Original UI fields (kept for compatibility, not sent to backend)
      // These are disabled as per todo.md requirements
      couponsSerial: "",
      sku: "",
      couponProduct: null as any,
      productSize: "",
      price: 0,
      refillTimesPerWeek: 0,
      itemCountPerOnce: 0,
    },
    validationSchema: Yup.object({
      // Backend field validation
      code: Yup.string()
        .trim()
        .required("Coupon code is required"),
      enName: Yup.string()
        .trim()
        .required("English name is required"),
      arName: Yup.string()
        .trim()
        .required("Arabic name is required"),
      discountValue: Yup.number()
        .required("Discount value is required")
        .min(0, "Discount value must be positive"),
      discountTypeId: Yup.number()
        .required("Discount type is required")
        .nullable(),
      expiryDate: Yup.string()
        .required("Expiry date is required"),
      isActive: Yup.boolean().required(),
      // Original UI fields - disabled and optional (not validated)
      couponsSerial: Yup.string().optional(),
      sku: Yup.string().optional(),
      couponProduct: Yup.mixed().optional(),
      productSize: Yup.string().optional(),
      price: Yup.number().optional(),
      refillTimesPerWeek: Yup.number().optional(),
      itemCountPerOnce: Yup.number().optional(),
    }),
    onSubmit: values => {
      const {
        // Backend fields - sent to API
        code,
        enName,
        arName,
        discountValue,
        discountTypeId,
        expiryDate,
        isActive,
      } = values

      if (isEditMode && couponId) {
        // Edit mode - dispatch updateCoupon
        dispatch(
          updateCoupon(
            couponId,
            {
              code,
              enName,
              arName,
              discountValue,
              discountTypeId: discountTypeId!,
              expiryDate,
              isActive,
            },
            { push: (path: string) => {} }
          )
        )
      } else {
        // Create mode - dispatch createCoupon
        dispatch(
          createCoupon(
            {
              code,
              enName,
              arName,
              discountValue,
              discountTypeId: discountTypeId!,
              expiryDate,
              isActive,
            },
            { push: (path: string) => {} }
          )
        )
      }

      validation.resetForm()
      toggle()
    },
  })

  useEffect(() => {
    if (isOpen) {
      // Increment key to force form remount when modal opens
      setKey(prev => prev + 1)
      // Reset form values based on mode
      if (isEditMode && selectedCoupon) {
        // Edit mode - form will be initialized with selectedCoupon data
      } else {
        // Create mode - reset to empty values
        validation.resetForm()
      }
      setSelectedProduct(null)
    }
  }, [isOpen])

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" key={key}>
      <ModalHeader toggle={toggle} tag="h3">
        <h4 className="text-capitalize fw-semibold font-size-24">
          {isEditMode ? t("coupons_editCoupon") : t("coupons_addNewCoupon")}
        </h4>
      </ModalHeader>
      <ModalBody>
        <Form
          onSubmit={e => {
            e.preventDefault()
            validation.handleSubmit()
            return false
          }}
        >
          {/* Backend Fields Section */}
          <Accordion open={openFirstAccordion}>
            <AccordionItem>
              <AccordionHeader
                targetId="0"
                onClick={() =>
                  setOpenFirstAccordion(openFirstAccordion === "0" ? "1" : "0")
                }
              >
                {t("common_basicInfo")}
              </AccordionHeader>
              <AccordionBody accordionId="0">
                <Row>
                  <Col md={6}>
                    <FormInput
                      label="Coupon Code"
                      name="code"
                      type="text"
                      placeholder="Enter coupon code"
                      validation={validation}
                      required
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      label="Discount Value"
                      name="discountValue"
                      type="number"
                      placeholder="Enter discount value"
                      validation={validation}
                      required
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <FormLabel htmlFor="discountTypeId" required>
                        Discount Type
                      </FormLabel>
                      <Select
                        id="discountTypeId"
                        value={discountTypeOptions.find(
                          (option: { value: number; label: string }) =>
                            option.value === validation.values.discountTypeId
                        )}
                        onChange={(selectedOption: any) => {
                          validation.setFieldValue(
                            "discountTypeId",
                            selectedOption?.value
                          )
                        }}
                        options={discountTypeOptions}
                        className="select2-selection"
                        placeholder="Select discount type"
                      />
                      {validation.touched.discountTypeId &&
                      validation.errors.discountTypeId ? (
                        <FormFeedback type="invalid" className="d-block">
                          {String(validation.errors.discountTypeId)}
                        </FormFeedback>
                      ) : null}
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormInput
                      label="Expiry Date"
                      name="expiryDate"
                      type="date"
                      validation={validation}
                      required
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <FormInput
                      label="English Name"
                      name="enName"
                      type="text"
                      placeholder="Enter English name"
                      validation={validation}
                      required
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      label="Arabic Name"
                      name="arName"
                      type="text"
                      placeholder="Enter Arabic name"
                      validation={validation}
                      required
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <div className="form-check form-switch mt-3">
                        <Input
                          type="switch"
                          role="switch"
                          id="isActive"
                          checked={validation.values.isActive}
                          onChange={e => {
                            validation.setFieldValue(
                              "isActive",
                              e.target.checked
                            )
                          }}
                        />
                        <Label className="form-check-label" htmlFor="isActive">
                          Active
                        </Label>
                      </div>
                    </FormGroup>
                  </Col>
                </Row>

                {/* UI-only fields - DISABLED as per todo.md requirements */}
                <p className="text-muted small mb-2">
                  <i>The following fields are display-only and do not affect the coupon data.</i>
                </p>
                <FormInput
                  label={t("coupons_couponSerial") as string}
                  name="couponsSerial"
                  type="text"
                  placeholder={t("coupons_enterCouponSerial") as string}
                  validation={validation}
                  disabled
                />
                <FormInput
                  label={t("coupons_sku") as string}
                  name="sku"
                  type="text"
                  placeholder={t("coupons_enterSku") as string}
                  validation={validation}
                  disabled
                />
                <FormGroup>
                  <FormLabel htmlFor="couponProduct">
                    {t("coupons_product")}
                  </FormLabel>
                  <Select
                    id="couponProduct"
                    value={selectedProduct}
                    onChange={(
                      selectedOption: { value: string; label: string } | null
                    ) => {
                      setSelectedProduct(selectedOption)
                      validation.setFieldValue("couponProduct", {
                        id: selectedOption?.value,
                        name: selectedOption?.label,
                      })
                    }}
                    options={productOptions.map(one => ({
                      value: one.id,
                      label: one.name,
                    }))}
                    className="select2-selection"
                    isDisabled
                  />
                </FormGroup>
                <FormInput
                  label={t("coupons_productSize") as string}
                  name="productSize"
                  type="number"
                  placeholder={t("coupons_enterCouponSize") as string}
                  validation={validation}
                  info={t("coupons_couponSizeInfo") as string}
                  disabled
                />
                <FormInput
                  label={t("common_priceCurrency") as string}
                  name="price"
                  type="number"
                  placeholder={t("coupons_enterprice") as string}
                  validation={validation}
                  info={t("coupons_priceInfo") as string}
                  disabled
                />
              </AccordionBody>
            </AccordionItem>
          </Accordion>

          {/* Water Refill Timing Section - DISABLED as per todo.md requirements */}
          <Accordion open={openThirdAccordion} className="mt-4">
            <AccordionItem>
              <AccordionHeader
                targetId="0"
                onClick={() =>
                  setOpenThirdAccordion(openThirdAccordion === "0" ? "1" : "0")
                }
              >
                {t("coupons_waterRefillTimeing")}
              </AccordionHeader>
              <AccordionBody accordionId="0">
                <FormInput
                  label={t("coupons_refillTimesPerWeek") as string}
                  name="refillTimesPerWeek"
                  type="number"
                  placeholder={t("coupons_enterRefillTimesPerWeek") as string}
                  validation={validation}
                  info={t("coupons_refillTimesPerWeekInfo") as string}
                  disabled
                />
                <FormInput
                  label={t("coupons_itemCountPerOnce") as string}
                  name="itemCountPerOnce"
                  type="number"
                  placeholder={t("coupons_enterItemCountPerOnce") as string}
                  validation={validation}
                  disabled
                />
              </AccordionBody>
            </AccordionItem>
          </Accordion>

          <Row>
            <Col>
              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  type="submit"
                  className={`btn bg-gradiant-blue text-white border-0 save-customer`}
                >
                  {isEditMode ? t("common_update") : t("common_create")}
                </button>
                <button
                  onClick={toggle}
                  type="button"
                  className="btn btn-cancel text-primary border border-cyan text-cyan"
                >
                  {t("common_cancel")}
                </button>
              </div>
            </Col>
          </Row>
        </Form>
      </ModalBody>
    </Modal>
  )
}
